import { useState } from 'react';
import { Input } from './ui/input';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from './ui/select';
import { MapPin, Plus, Trash2, ChevronDown, ChevronUp, Star } from 'lucide-react';
import { cn } from '../lib/utils';

const ADDRESS_TYPES = [
  { value: 'mailing', label: 'Mailing' },
  { value: 'billing', label: 'Billing' },
  { value: 'shipping', label: 'Shipping' },
  { value: 'other', label: 'Other' },
];

const COUNTRIES = [
  { value: 'US', label: 'United States' },
  { value: 'CA', label: 'Canada' },
  { value: 'GB', label: 'United Kingdom' },
  { value: 'AU', label: 'Australia' },
  { value: 'OTHER', label: 'Other' },
];

const TYPE_COLORS = {
  mailing: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300',
  billing: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300',
  shipping: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300',
  other: 'bg-muted text-muted-foreground',
};

const newAddress = (overrides = {}) => ({
  address_id: `tmp_${Math.random().toString(36).slice(2, 10)}`,
  type: 'mailing',
  label: '',
  line1: '',
  line2: '',
  city: '',
  state: '',
  zip_code: '',
  country: 'US',
  is_primary: false,
  ...overrides,
});

export function MultiAddressEditor({ value = [], onChange }) {
  const addresses = value.length > 0 ? value : [newAddress({ is_primary: true })];
  const [expandedIds, setExpandedIds] = useState(new Set([addresses[0]?.address_id]));

  const update = (idx, patch) => {
    onChange(addresses.map((a, i) => (i === idx ? { ...a, ...patch } : a)));
  };

  const remove = (idx) => {
    let next = addresses.filter((_, i) => i !== idx);
    if (next.length > 0 && !next.some(a => a.is_primary)) {
      next = next.map((a, i) => i === 0 ? { ...a, is_primary: true } : a);
    }
    onChange(next);
  };

  const add = () => {
    const a = newAddress();
    onChange([...addresses, a]);
    setExpandedIds(new Set([...expandedIds, a.address_id]));
  };

  const setPrimary = (idx) => {
    onChange(addresses.map((a, i) => ({ ...a, is_primary: i === idx })));
  };

  const toggleExpand = (id) => {
    const next = new Set(expandedIds);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setExpandedIds(next);
  };

  const formatLine = (a) => {
    const parts = [a.line1, a.city, [a.state, a.zip_code].filter(Boolean).join(' ')].filter(Boolean);
    return parts.join(', ') || 'New address';
  };

  return (
    <div className="space-y-3" data-testid="multi-address-editor">
      {addresses.map((a, idx) => {
        const isExpanded = expandedIds.has(a.address_id) || addresses.length === 1;
        return (
          <div
            key={a.address_id}
            className={cn(
              'rounded-xl border transition-colors',
              a.is_primary ? 'border-primary/40 bg-primary/5' : 'border-border bg-card'
            )}
            data-testid={`address-row-${idx}`}
          >
            {/* Collapsed header */}
            <div className="flex items-center gap-2 p-3">
              <button
                type="button"
                className="flex items-center gap-2 flex-1 text-left min-w-0"
                onClick={() => toggleExpand(a.address_id)}
                data-testid={`address-toggle-${idx}`}
              >
                <MapPin className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-1.5 flex-wrap">
                    <Badge variant="secondary" className={cn('text-[10px] capitalize', TYPE_COLORS[a.type])}>
                      {ADDRESS_TYPES.find(t => t.value === a.type)?.label || a.type}
                    </Badge>
                    {a.is_primary && <Badge className="text-[10px] bg-primary text-primary-foreground">Primary</Badge>}
                    {a.label && <span className="text-xs text-muted-foreground">— {a.label}</span>}
                  </div>
                  <p className="text-xs text-muted-foreground truncate mt-0.5">
                    {formatLine(a)}
                  </p>
                </div>
                {isExpanded ? <ChevronUp className="w-4 h-4 text-muted-foreground" /> : <ChevronDown className="w-4 h-4 text-muted-foreground" />}
              </button>
              <Button
                type="button"
                size="icon"
                variant={a.is_primary ? 'default' : 'ghost'}
                className="h-8 w-8 flex-shrink-0"
                onClick={() => setPrimary(idx)}
                title={a.is_primary ? 'Primary address' : 'Make primary'}
                data-testid={`address-primary-${idx}`}
              >
                <Star className={cn('w-3.5 h-3.5', a.is_primary && 'fill-current')} />
              </Button>
              {addresses.length > 1 && (
                <Button
                  type="button"
                  size="icon"
                  variant="ghost"
                  className="h-8 w-8 text-destructive flex-shrink-0"
                  onClick={() => remove(idx)}
                  data-testid={`address-remove-${idx}`}
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </Button>
              )}
            </div>

            {/* Expanded form */}
            {isExpanded && (
              <div className="px-3 pb-3 pt-1 space-y-2 border-t">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 pt-2">
                  <Select value={a.type} onValueChange={(v) => update(idx, { type: v })}>
                    <SelectTrigger className="h-9" data-testid={`address-type-${idx}`}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {ADDRESS_TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <Input
                    placeholder="Label (optional, e.g. Warehouse 2)"
                    value={a.label || ''}
                    onChange={(e) => update(idx, { label: e.target.value })}
                    className="h-9"
                    data-testid={`address-label-${idx}`}
                  />
                </div>
                <Input
                  placeholder="Street address"
                  value={a.line1 || ''}
                  onChange={(e) => update(idx, { line1: e.target.value })}
                  className="h-9"
                  data-testid={`address-line1-${idx}`}
                />
                <Input
                  placeholder="Apt, suite, unit (optional)"
                  value={a.line2 || ''}
                  onChange={(e) => update(idx, { line2: e.target.value })}
                  className="h-9"
                  data-testid={`address-line2-${idx}`}
                />
                <div className="grid grid-cols-2 gap-2">
                  <Input
                    placeholder="City"
                    value={a.city || ''}
                    onChange={(e) => update(idx, { city: e.target.value })}
                    className="h-9"
                    data-testid={`address-city-${idx}`}
                  />
                  <Input
                    placeholder="State"
                    value={a.state || ''}
                    onChange={(e) => update(idx, { state: e.target.value })}
                    className="h-9"
                    data-testid={`address-state-${idx}`}
                  />
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <Input
                    placeholder="ZIP / Postal Code"
                    value={a.zip_code || ''}
                    onChange={(e) => update(idx, { zip_code: e.target.value })}
                    className="h-9"
                    data-testid={`address-zip-${idx}`}
                  />
                  <Select value={a.country || 'US'} onValueChange={(v) => update(idx, { country: v })}>
                    <SelectTrigger className="h-9" data-testid={`address-country-${idx}`}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {COUNTRIES.map(c => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            )}
          </div>
        );
      })}

      <Button
        type="button"
        variant="outline"
        size="sm"
        onClick={add}
        className="w-full sm:w-auto"
        data-testid="add-address-btn"
      >
        <Plus className="w-3.5 h-3.5 mr-1" /> Add address
      </Button>
    </div>
  );
}

export default MultiAddressEditor;
